import * as React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LoadingSpinner } from "@/components/common/LoadingSpinner";
import { useLanguage } from "@/contexts/LanguageContext";

export function ConfirmEmailPage() {
  const { confirmEmail, resendConfirmation } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const initialEmail = (location.state as { email?: string } | null)?.email ?? "";
  const [email, setEmail] = React.useState(initialEmail);
  const [token, setToken] = React.useState("");
  const [message, setMessage] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const isValidToken = /^(?:\d{6}|\d{8})$/.test(token);

  async function verify(event: React.FormEvent) {
    if (!isValidToken) { event.preventDefault(); return; }
    event.preventDefault(); setBusy(true); setError(null); setMessage(null);
    try {
      await confirmEmail(email.trim(), token.trim());
      navigate("/dashboard", { replace: true });
    } catch { setError(t("The code is invalid or expired. Request a new one.")); }
    finally { setBusy(false); }
  }

  async function resend() {
    if (!email.trim()) { setError(t("Enter the email you used to create your account.")); return; }
    setBusy(true); setError(null);
    try { await resendConfirmation(email.trim()); setMessage(t("We sent a new code. Check your spam folder too.")); }
    catch { setError(t("We could not resend the code. Wait a minute and try again.")); }
    finally { setBusy(false); }
  }

  return <div className="flex flex-col gap-6">
    <div><h1 className="font-display text-2xl font-semibold">{t("Confirm your email")}</h1>
      <p className="mt-1 text-sm text-muted-foreground">{t("Enter the complete verification code sent to your email.")}</p></div>
    <form onSubmit={verify} className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5"><Label htmlFor="confirmEmail">{t("Email")}</Label>
        <Input id="confirmEmail" type="email" required value={email} onChange={(e)=>setEmail(e.target.value)} /></div>
      <div className="flex flex-col gap-1.5"><Label htmlFor="confirmationCode">{t("Verification code")}</Label>
        <Input id="confirmationCode" inputMode="numeric" autoComplete="one-time-code" maxLength={8} pattern="([0-9]{6}|[0-9]{8})" required value={token} onChange={(e)=>setToken(e.target.value.replace(/\D/g, "").slice(0, 8))} placeholder="12345678" /></div>
      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
      {message && <p role="status" className="text-sm text-primary">{message}</p>}
      <Button type="submit" disabled={busy || !isValidToken}>{busy && <LoadingSpinner />}{t("Confirm account")}</Button>
      <Button type="button" variant="outline" disabled={busy} onClick={resend}>{t("Send a new code")}</Button>
    </form>
    <Link to="/login" className="text-sm text-primary hover:underline">{t("Back to sign in")}</Link>
  </div>;
}
